import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  closeManufacturingLaborPayWeek,
  listManufacturingLaborPayList,
  previewManufacturingLaborPayWeek,
  requestManufacturingLaborPay,
  reviewManufacturingLaborPayList,
  saveManufacturingLaborPayCutoff,
  type LaborPayListRow,
} from "@/lib/manufacturing/laborPayListClient";
import {
  canCloseLaborPayWeek,
  canRequestLaborPayment,
  canStampLaborPay,
  type LaborPayReviewSide,
} from "@/lib/manufacturing/laborPayList";
import type { LaborPayCutoffConfig } from "@/lib/manufacturing/laborPayWeek";

export function useManufacturingLaborPayList(plantId: string, navTick: number) {
  const auth = useAuth();
  const roles = auth.roles ?? [];
  const [rows, setRows] = useState<LaborPayListRow[]>([]);
  const [missingTable, setMissingTable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cutoffLabel, setCutoffLabel] = useState<string | null>(null);
  const [cutoffConfig, setCutoffConfig] = useState<LaborPayCutoffConfig | null>(null);

  const reload = useCallback(async () => {
    if (!plantId) {
      setRows([]);
      return;
    }
    setBusy(true);
    setError(null);
    const res = await listManufacturingLaborPayList(plantId);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setRows(res.data.rows);
    setMissingTable(res.data.missing_table);
    const preview = await previewManufacturingLaborPayWeek();
    if (preview.ok) {
      setCutoffLabel(new Date(preview.data.preview.cutoff_at).toLocaleString());
      setCutoffConfig(preview.data.preview.config);
    }
  }, [plantId]);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  const stamp = useCallback(
    async (row: LaborPayListRow, side: LaborPayReviewSide) => {
      setActionBusy(true);
      setError(null);
      const res = await reviewManufacturingLaborPayList({
        entry_id: row.entry_id,
        unit_id: row.unit_id,
        side,
      });
      setActionBusy(false);
      if (!res.ok) {
        setError(res.error);
        return false;
      }
      await reload();
      return true;
    },
    [reload]
  );

  const requestPay = useCallback(
    async (row: LaborPayListRow) => {
      if (!row.entry_id) return false;
      setActionBusy(true);
      setError(null);
      const res = await requestManufacturingLaborPay(row.entry_id);
      setActionBusy(false);
      if (!res.ok) {
        setError(res.error);
        return false;
      }
      await reload();
      return true;
    },
    [reload]
  );

  const closeWeek = useCallback(async () => {
    setActionBusy(true);
    setError(null);
    const res = await closeManufacturingLaborPayWeek();
    setActionBusy(false);
    if (!res.ok) {
      setError(res.error);
      return false;
    }
    await reload();
    return true;
  }, [reload]);

  const saveCutoff = useCallback(async (next: LaborPayCutoffConfig) => {
    setCutoffConfig(next);
    setActionBusy(true);
    setError(null);
    const res = await saveManufacturingLaborPayCutoff(next);
    setActionBusy(false);
    if (!res.ok) {
      setError(res.error);
      return false;
    }
    setCutoffLabel(new Date(res.data.preview.cutoff_at).toLocaleString());
    setCutoffConfig(res.data.preview.config);
    return true;
  }, []);

  return {
    rows,
    missingTable,
    busy,
    actionBusy,
    error,
    cutoffLabel,
    cutoffConfig,
    canOps: canStampLaborPay(roles, "ops"),
    canPlant: canStampLaborPay(roles, "plant"),
    canCloseWeek: canCloseLaborPayWeek(roles),
    canRequest: (row: LaborPayListRow) => canRequestLaborPayment(roles, row.status),
    stamp,
    requestPay,
    closeWeek,
    saveCutoff,
    reload,
  };
}
